import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import Loading from 'components/LoadingComponent';
import SwipeableCards from 'components/Steps/SwipeableCards';
import UserSmallCard from 'components/Cards/UserSmallCard/components';
import SimpleCarousel from 'components/SimpleCarousel';
import Agenda from 'components/Agenda';
import NoResultsImg from 'assets/images/think/no_think_chats.svg';
import ConnectedUser from 'assets/images/user/connectedWithUserBlack.svg';
import callIcon from 'assets/images/calls/callIconBlack.svg';
import ArrowBack from 'assets/images/ArrowBack.svg';
import tableIcon from 'assets/images/calls/tableBlack.svg';
import { EVENT, TABLE } from 'constants/speed_dating';
import { SPEED_DATING } from 'constants/types';
import speedDatingLogo from 'assets/images/hall/speedDatingLogoGreen.svg';
import clockIcon from 'assets/images/calls/clock.svg';
import {
  getSpeedDatingRecommendations, sendSpeedDatingUserFeedback, getSpeedDatingSelectedUsers,
  getSpeedDatingData,
} from 'modules/speedDating/actions';
import {
  SpeedDatingWrapper, LoadingWrapper, Header, NoElements,
} from './styles';

const MAX_CARDS = 10;
function SpeedDating(props) {
  const {
    literals, setBreadcrumb, resetAll, app, meetings,
    newUsersInNarratives, actualUser, cleanUsersFromNarrative,
    match: { params: { id } },
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [usersViewed, setUsersViewed] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [nextRecommendations, setNextRecommendations] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [data, setData] = useState(null);

  const loadUsersToChoose = () => {
    getSpeedDatingRecommendations(id)
      .then((response) => {
        if (response.data.length || recommendations === null) {
          const next = [...response.data];
          const actual = next.splice(0, MAX_CARDS);
          setRecommendations([...actual]);
          setNextRecommendations(next);
        }
        cleanUsersFromNarrative(id);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const loadSelectedUsers = () => {
    getSpeedDatingSelectedUsers(id)
      .then((response) => {
        setSelectedUsers(response.data);
      });
  };

  const loadSpeedDatingData = () => {
    getSpeedDatingData(id)
      .then((response) => {
        setData(response);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const loadInitialData = () => {
    loadUsersToChoose();
    loadSelectedUsers();
    loadSpeedDatingData();
  };

  useEffect(() => {
    if (data !== null && recommendations !== null) {
      setIsLoading(false);
    }
  }, [data, recommendations]);

  useEffect(() => {
    if (data && data.element && newUsersInNarratives[data.element.id] && newUsersInNarratives[data.element.id].length) {
      const scheduleMeetings = _.filter(meetings, { element_id: parseInt(id, 10) });
      newUsersInNarratives[data.element.id].forEach((user) => {
        const { id: userId } = user;
        if (userId !== actualUser.id && recommendations !== null && !_.find(recommendations, { id: userId }) && !_.find(nextRecommendations, { id: userId }) && !_.find(usersViewed, { id: userId }) && !_.find(selectedUsers, { id: userId }) && !_.find(scheduleMeetings, { user: { id: userId } })) {
          if (!recommendations.length) {
            recommendations.push({ ...user });
            setRecommendations([...recommendations]);
          } else {
            nextRecommendations.push({ ...user });
            setNextRecommendations([...nextRecommendations]);
          }
        }
      });
      // cleanUsersFromNarrative(data.element.id);
    }
  }, [newUsersInNarratives]);

  // Breadcrumb
  useEffect(() => {
    const breadcrumb = [
      {
        color: '#ffffff',
        text: literals.title,
      },
    ];

    setBreadcrumb(breadcrumb.slice());
    resetAll();
    loadInitialData();
  }, []);

  // Event Handlers
  const sendUserFeedback = (userId, feedback) => {
    sendSpeedDatingUserFeedback(id, userId, feedback);
  };

  const handleSelectUser = (userId) => {
    sendUserFeedback(userId, 1);
    const user = _.find(recommendations, { id: userId });
    if (user) {
      selectedUsers.push(user);
      setSelectedUsers([...selectedUsers]);
    }
  };

  const handleRejectUser = (userId) => {
    sendUserFeedback(userId, 0);
  };

  const handleFinishCarousel = () => {
    setUsersViewed(usersViewed.concat([...recommendations]));
    setRecommendations([]);
  };

  // Cuando nos quedamos sin recomendaciones miramos si tenemos mas
  // en la variable nextRecommendations
  // Si no tenemos llamamos al servidor
  // Pero esperamos unos segundos para asegurarnos de que el feedback de la última card se ha guardado
  let toSearchRecommendations = null;
  useEffect(() => {
    if (nextRecommendations.length && !recommendations.length) {
      const pendingRecommendations = [...nextRecommendations];
      const next = pendingRecommendations.splice(0, MAX_CARDS);
      setRecommendations([...next]);
      setNextRecommendations([...pendingRecommendations]);
    } else if (recommendations !== null && !nextRecommendations.length && !recommendations.length) {
      if (toSearchRecommendations) {
        clearTimeout(toSearchRecommendations);
      }
      toSearchRecommendations = setTimeout(() => {
        if (!recommendations.length) {
          loadUsersToChoose();
        }
        toSearchRecommendations = null;
      }, 5000);
    }

    return (() => {
      if (toSearchRecommendations) {
        clearTimeout(toSearchRecommendations);
      }
    });
  }, [recommendations]);

  const handleGoBack = () => {
    const { history } = props;
    history.goBack();
  };

  // Print Functions
  const userCarousel = useMemo(() => {
    if (!recommendations || !recommendations.length) {
      return (
        <NoElements>
          <div className='nocontent'>
            <img src={NoResultsImg} alt='' />
            <div className='text'>{literals.noUsers}</div>
          </div>
        </NoElements>
      );
    }

    return (
      <SwipeableCards
        users={_.map(recommendations, ({ id: userId }) => userId)}
        onEnd={handleFinishCarousel}
        onYup={handleSelectUser}
        onNope={handleRejectUser}
        alwaysShowOptions
        type={SPEED_DATING}
      />
    );
  }, [recommendations]);
  const printRecommendations = () => {
    return userCarousel;
  };

  const printScheduleMeetings = () => {
    const scheduleMeetings = _.sortBy(_.filter(meetings, { element_id: parseInt(id, 10) }), ['start_date']);

    if (!scheduleMeetings.length && !data) {
      return null;
    }

    return (
      <Agenda speedDatings={[data]} showPlanSpeedDating={false} showRandomSpeedDating scheduleSpeedDatings={scheduleMeetings} title={literals.scheduledSpeedDatings} />
    );
  };

  const carouselSelectedUsers = useMemo(() => {
    const interestUsers = [];

    const scheduleMeetings = _.filter(meetings, { element_id: parseInt(id, 10) });
    selectedUsers.forEach((user) => {
      if (!_.find(scheduleMeetings, { user: { id: user.id } })) {
        interestUsers.push(user);
      }
    });

    if (!interestUsers.length) {
      return null;
    }

    return (
      <div className='matches'>
        <div>
          <img src={ConnectedUser} alt='' />
          <span className='title' dangerouslySetInnerHTML={{ __html: literals.youHaveShownInterest }} />
        </div>
        <SimpleCarousel classesList='user_wrapper'>
          {_.map(interestUsers, selectedUser => (
            <UserSmallCard key={selectedUser.id} user={selectedUser} />
          ))}
        </SimpleCarousel>
      </div>
    );
  }, [selectedUsers, meetings]);

  const printSelectedUsers = () => {
    return carouselSelectedUsers;
  };

  const printHeader = () => {
    let icon = null;
    if (!data) {
      return null;
    }
    switch (data.element_type.id) {
      case EVENT:
        icon = callIcon;
        break;
      case TABLE:
        icon = tableIcon;
        break;
      default:
        break;
    }

    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const datetime = new Date(data.start_date * 1000);
    const month = months[datetime.getMonth()];
    const day = datetime.getDate();
    let dayOfMonth = '';
    if (day === 1 || day === 21 || day === 31) {
      dayOfMonth = `${day}st`;
    } else if (day === 2 || day === 22) {
      dayOfMonth = `${day}nd`;
    } else if (day === 3 || day === 23) {
      dayOfMonth = `${day}rd`;
    } else {
      dayOfMonth = `${day}th`;
    }

    return (
      <Header>
        {icon ? (
          <div className='icon'>
            <img className='back' src={ArrowBack} alt='' onClick={handleGoBack} />
            <img src={icon} alt='' />
          </div>
        ) : null}
        <div className='description'>
          {`${literals.titleDesc} `}
          <strong>{data.element.name}</strong>
        </div>
        <div className='card_wrapper'>
          <div className='card_info'>
            <div className='header'>
              <div className='icon'>
                <img src={speedDatingLogo} alt='' />
              </div>
              <div className='data'>{`${month} ${dayOfMonth}`}</div>
              <div className='type'>
                {data.element_type.id === EVENT && literals.event}
                {data.element_type.id === TABLE && literals.table}
              </div>
            </div>
            <div className='body'>
              <img src={clockIcon} alt='' />
              <div className='data'>
                {`${literals.startingAt} ${moment(data.start_date, 'X').format('hh:mm A')}`}
              </div>
            </div>
          </div>
        </div>
      </Header>
    );
  };

  if (isLoading) {
    return (<LoadingWrapper><Loading colorAnimation={app.color1} /></LoadingWrapper>);
  }

  return (
    <SpeedDatingWrapper>
      {printHeader()}
      <div className='content'>
        {printRecommendations()}
        {printScheduleMeetings()}
        {printSelectedUsers()}
      </div>
    </SpeedDatingWrapper>
  );
}

SpeedDating.propTypes = {
  match: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  literals: PropTypes.object.isRequired,
  newUsersInNarratives: PropTypes.object.isRequired,
  actualUser: PropTypes.object.isRequired,
  setBreadcrumb: PropTypes.func.isRequired,
  resetAll: PropTypes.func.isRequired,
  cleanUsersFromNarrative: PropTypes.func.isRequired,
  meetings: PropTypes.array,
};

SpeedDating.defaultProps = {
  meetings: [],
};

export default SpeedDating;



// WEBPACK FOOTER //
// ./src/routes/SpeedDating/components/index.jsx